import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  User, 
  Building2, 
  Smartphone, 
  Monitor, 
  Clock, 
  ShieldCheck, 
  Lock, 
  Package, 
  FileText, 
  CheckCircle2, 
  ShieldAlert, 
  Activity, 
  LogOut,
  Edit2,
  Key,
  DollarSign
} from 'lucide-react';
import { IdentityService } from '@/services/IdentityService';
import { logAuditEvent } from '@/utils/AuditLogManager';

export default function ClientAccountDetailPage({ client, onBack }) {
  const [activeTab, setActiveTab] = useState('Overview');
  const [is2FAEnabled, setIs2FAEnabled] = useState(client?.has2FA || false);
  const [accountStatus, setAccountStatus] = useState(client?.status || 'Aktif');

  // Client Orders Integration Dataset
  const clientOrders = [
    { id: 'PO-2026-0891', date: '2026-08-01', product: 'Parfum Signature 50ml', qty: '1.000 Pcs', total: 'Rp 45.000.000', status: 'Dalam Produksi' },
    { id: 'PO-2026-0412', date: '2026-05-15', product: 'Body Mist Bloom 100ml', qty: '2.000 Pcs', total: 'Rp 68.000.000', status: 'Selesai & Dikirim' }
  ];

  // Activity Vertical Timeline Dataset
  const activityTimeline = [
    { time: 'Hari ini • 09:22', title: 'Masuk Akun (Login)', icon: '🟢', details: 'Login sukses via Safari di iPhone 13 (IP: 103.50.12.14)' },
    { time: 'Hari ini • 09:24', title: 'Download Document Invoice', icon: '📄', details: 'Mengunduh invoice PO-2026-0891 (Format PDF)' },
    { time: 'Hari ini • 09:28', title: 'Ubah Data Profil Alamat', icon: '✏️', details: 'Memperbarui alamat pengiriman utama' },
    { time: 'Kemarin • 14:10', title: 'Tarik Ulang SPH / Penawaran', icon: '📦', details: 'Mengajukan estimasi HPP sampel baru' },
    { time: '3 hari lalu', title: 'Logout Akun', icon: '🚪', details: 'Sesi berakhir di Chrome Windows 10' }
  ];

  const handleForceLogoutAll = () => {
    IdentityService.forceLogoutAllDevices(client.id);
    toast.error(`🔒 Semua sesi login untuk ${client.name} (${client.company}) telah ditutup paksa!`);
  };

  const handleToggle2FA = () => {
    const nextVal = !is2FAEnabled;
    setIs2FAEnabled(nextVal);
    toast.info(`Status 2FA ${client.name} diubah menjadi ${nextVal ? 'AKTIF' : 'NONAKTIF'}`);
  };

  return (
    <div className="p-6 space-y-6 font-sans bg-[#F8F8F6] min-h-screen text-slate-800">
      
      {/* Back Button */}
      <button 
        onClick={onBack} 
        className="inline-flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-[#A97838] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Kembali ke Daftar Client Accounts
      </button>

      {/* ─── HEADER PROFILE BANNER ─── */}
      <div className="bg-white border border-[#ECECEC] p-6 rounded-2xl shadow-xs flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#302621] text-[#A97838] font-black text-2xl flex items-center justify-center border-2 border-[#A97838] shadow-sm">
            {client?.avatar || 'C'}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black text-slate-900 tracking-tight">{client?.name || 'Client Account'}</h1>
              <span className="bg-[#A97838] text-white font-bold text-[10px] px-2.5 py-0.5 rounded-full">{client?.role || 'Client'}</span>
              <span className="inline-flex items-center gap-1 text-[10px] font-extrabold bg-emerald-50 text-[#20C05C] px-2 py-0.5 rounded-full border border-emerald-200">
                <span className="w-1.5 h-1.5 bg-[#20C05C] rounded-full animate-pulse"></span> {client?.onlineStatus || 'Online'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Perusahaan: <span className="font-bold text-[#A97838]">{client?.company || 'Brand Client'}</span> • Email: <span className="font-mono font-bold text-slate-700">{client?.email}</span></p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button 
            onClick={handleForceLogoutAll}
            variant="outline" 
            className="h-10 text-xs font-bold border-rose-200 text-rose-600 hover:bg-rose-50 rounded-xl gap-1.5"
          >
            <LogOut className="w-4 h-4" /> Force Logout All Devices
          </Button>
        </div>
      </div>

      {/* ─── NAVIGATION TABS ─── */}
      <div className="flex border-b border-[#ECECEC] gap-2 overflow-x-auto pb-0.5">
        {['Overview', 'Profile', 'Devices', 'Activity', 'Orders', 'Security', 'Permission', 'Notes'].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-3 px-5 text-xs font-bold transition-all border-b-2 whitespace-nowrap ${
              activeTab === tab 
                ? 'border-[#A97838] text-[#A97838]' 
                : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ─── TAB 1: OVERVIEW ─── */}
      {activeTab === 'Overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-white border-[#ECECEC] p-5 space-y-3">
            <h4 className="font-bold text-xs text-slate-400 uppercase tracking-wider">Status Akun CRM</h4>
            <p className="text-2xl font-black text-[#20C05C]">{accountStatus}</p>
            <p className="text-xs text-slate-500">Terdaftar sejak: {client?.createdDate || '2024-01-01'}</p>
          </Card>

          <Card className="bg-white border-[#ECECEC] p-5 space-y-3">
            <h4 className="font-bold text-xs text-slate-400 uppercase tracking-wider">Total Nilai Order Maklon</h4>
            <p className="text-2xl font-black text-[#A97838]">Rp 113.000.000</p>
            <p className="text-xs text-slate-500">Dari 2 Purchase Order sukses</p>
          </Card>

          <Card className="bg-white border-[#ECECEC] p-5 space-y-3">
            <h4 className="font-bold text-xs text-slate-400 uppercase tracking-wider">Perangkat Aktif Login</h4>
            <p className="text-2xl font-black text-slate-900">2 Devices</p>
            <p className="text-xs text-emerald-600 font-bold">1 Device Online Sekarang</p>
          </Card>
        </div>
      )}

      {/* ─── TAB 2: PROFILE ─── */}
      {activeTab === 'Profile' && (
        <Card className="bg-white border-[#ECECEC] p-6 max-w-2xl space-y-4">
          <h3 className="font-bold text-sm text-slate-900 border-b border-[#ECECEC] pb-3">Informasi Detail Profil Client</h3>
          <div className="space-y-3 text-xs">
            <div className="flex justify-between"><span className="text-slate-400">Nama Lengkap</span><span className="font-bold text-slate-800">{client?.name}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Email Utama</span><span className="font-mono font-bold text-slate-800">{client?.email}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Nomor WhatsApp</span><span className="font-mono font-bold text-slate-800">{client?.phone}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Nama Perusahaan / Brand</span><span className="font-bold text-[#A97838]">{client?.company}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Alamat Utama</span><span className="font-bold text-slate-800">Jakarta Selatan, Indonesia</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Role Akses</span><span className="font-bold text-slate-800">{client?.role}</span></div>
          </div>
        </Card>
      )}

      {/* ─── TAB 3: DEVICES ─── */}
      {activeTab === 'Devices' && (
        <div className="space-y-4 max-w-3xl">
          <h3 className="font-bold text-sm text-slate-900">Daftar Perangkat Terhubung ({client?.name})</h3>
          
          <Card className="bg-white border-[#ECECEC] p-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Smartphone className="w-6 h-6 text-[#A97838]" />
              <div>
                <p className="font-bold text-xs text-slate-900">iPhone 13 • Safari 17 (iOS 17.2)</p>
                <p className="text-[10px] text-slate-400">IP: 103.50.12.14 • Lokasi: Jakarta, Indonesia</p>
              </div>
            </div>
            <span className="text-[10px] font-bold text-[#20C05C] bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">Online</span>
          </Card>

          <Card className="bg-white border-[#ECECEC] p-4 flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Monitor className="w-6 h-6 text-slate-400" />
              <div>
                <p className="font-bold text-xs text-slate-900">Desktop PC • Chrome 121 (Windows 10)</p>
                <p className="text-[10px] text-slate-400">IP: 103.50.12.14 • Terakhir aktif: 1 hari lalu</p>
              </div>
            </div>
            <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded">Offline</span>
          </Card>
        </div>
      )}

      {/* ─── TAB 4: ACTIVITY VERTICAL TIMELINE ─── */}
      {activeTab === 'Activity' && (
        <div className="space-y-4 max-w-2xl">
          <h3 className="font-bold text-sm text-slate-900">Jejak Aktivitas (Activity Timeline)</h3>
          
          <div className="relative border-l-2 border-[#A97838]/30 ml-4 space-y-6 pt-2">
            {activityTimeline.map((item, idx) => (
              <div key={idx} className="relative pl-6">
                <div className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-white border-2 border-[#A97838] text-base flex items-center justify-center shadow-xs">
                  {item.icon}
                </div>
                <div className="bg-white border border-[#ECECEC] p-4 rounded-xl shadow-2xs space-y-1">
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-xs text-slate-900">{item.title}</h4>
                    <span className="font-mono text-[10px] text-slate-400">{item.time}</span>
                  </div>
                  <p className="text-xs text-slate-600">{item.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── TAB 5: ORDERS INTEGRATION ─── */}
      {activeTab === 'Orders' && (
        <Card className="bg-white border-[#ECECEC] overflow-hidden">
          <div className="p-4 border-b border-[#ECECEC]">
            <h3 className="font-bold text-sm text-slate-900">Riwayat Purchase Order (PO) & Maklon</h3>
          </div>
          <table className="w-full text-left text-xs">
            <thead className="bg-[#FAF8F5] text-slate-600 font-bold border-b border-[#ECECEC]">
              <tr>
                <th className="py-3 px-4">No. PO</th>
                <th className="py-3 px-4">Tanggal</th>
                <th className="py-3 px-4">Produk Parfum</th>
                <th className="py-3 px-4">Qty</th>
                <th className="py-3 px-4">Total Invoice</th>
                <th className="py-3 px-4 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ECECEC]">
              {clientOrders.map(o => (
                <tr key={o.id} className="hover:bg-slate-50">
                  <td className="py-3 px-4 font-mono font-bold text-[#A97838]">{o.id}</td>
                  <td className="py-3 px-4 text-slate-500 font-mono">{o.date}</td>
                  <td className="py-3 px-4 font-bold text-slate-800">{o.product}</td>
                  <td className="py-3 px-4 font-mono text-slate-600">{o.qty}</td>
                  <td className="py-3 px-4 font-bold text-slate-900">{o.total}</td>
                  <td className="py-3 px-4 text-center">
                    <span className="font-bold text-[10px] bg-emerald-50 text-[#20C05C] px-2.5 py-0.5 rounded-full border border-emerald-200">
                      {o.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ─── TAB 6: SECURITY ─── */}
      {activeTab === 'Security' && (
        <Card className="bg-white border-[#ECECEC] p-6 max-w-2xl space-y-6">
          <h3 className="font-bold text-sm text-slate-900 border-b border-[#ECECEC] pb-3">Kontrol Keamanan Akun</h3>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-xs text-slate-900">Autentikasi Dua Langkah (2FA)</p>
              <p className="text-[11px] text-slate-400">Menambah lapisan keamanan saat login ke Client Portal.</p>
            </div>
            <Button onClick={handleToggle2FA} variant="outline" className={`h-8 text-xs font-bold ${is2FAEnabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {is2FAEnabled ? '✓ 2FA Aktif' : 'Aktifkan 2FA'}
            </Button>
          </div>

          <hr className="border-[#ECECEC]" />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-xs text-slate-900">Kirim Tautan Reset Password</p>
              <p className="text-[11px] text-slate-400">Mengirim email instruksi ubah sandi ke email client.</p>
            </div>
            <Button onClick={() => toast.success(`Tautan reset password terkirim ke ${client?.email}`)} variant="outline" className="h-8 text-xs font-bold border-[#ECECEC] hover:bg-slate-50">
              Kirim Reset Email
            </Button>
          </div>

          <hr className="border-[#ECECEC]" />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-bold text-xs text-rose-600">Nonaktifkan / Suspend Akun</p>
              <p className="text-[11px] text-slate-400">Memblokir akses login sementara untuk akun ini.</p>
            </div>
            <Button onClick={() => toast.warning(`Akun ${client?.name} telah di-suspend!`)} variant="destructive" className="h-8 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white">
              Suspend Akun
            </Button>
          </div>
        </Card>
      )}

      {/* ─── TAB 7 & 8: PERMISSION & NOTES ─── */}
      {(activeTab === 'Permission' || activeTab === 'Notes') && (
        <Card className="bg-white border-[#ECECEC] p-6 max-w-2xl">
          <p className="text-xs text-slate-500 font-semibold">Pengaturan {activeTab} siap dikonfigurasi.</p>
        </Card>
      )}

    </div>
  );
}
